/**
 * Language selection context and provider for multilanguage text input components.
 *
 * This module provides a React context that manages the currently selected language
 * in a multilanguage input scenario. It allows multiple components within a component
 * tree to share and synchronize the active language selection.
 */

import { isNil } from "ramda";
import React, { Dispatch, PropsWithChildren, SetStateAction, useContext, useState } from "react";

/**
 * Context for sharing language selection state across components.
 * The value is a tuple of [selectedLanguage, setSelectedLanguage] similar to useState.
 */
const LanguageSelectedContext = React.createContext<[string | null, Dispatch<SetStateAction<string>>] | null>(null);
LanguageSelectedContext.displayName = "LanguageSelectedContext";

/**
 * Props for the LanguageSelectedProvider component.
 */
interface LanguageSelectedProviderProps extends PropsWithChildren {
  /**
   * The initial language to select. If not provided, defaults to null.
   */
  languageSelected?: string;
}

/**
 * Provider component that shares language selection state with descendant components.
 *
 * This provider creates a shared state for the currently selected language that can be
 * accessed by any descendant component using the useLanguageSelected hook.
 *
 * @param props - Component props including children and optional initial language
 * @returns Provider component wrapping children with language selection context
 *
 * @example
 * ```tsx
 * <LanguageSelectedProvider languageSelected="en">
 *   <MultiLanguageForm />
 * </LanguageSelectedProvider>
 * ```
 */
export function LanguageSelectedProvider({ children, languageSelected }: LanguageSelectedProviderProps) {
  const state = useState(languageSelected ?? null) as [string | null, Dispatch<SetStateAction<string>>];
  return <LanguageSelectedContext.Provider value={state}>{children}</LanguageSelectedContext.Provider>;
}

/**
 * Hook to access and manage the currently selected language.
 *
 * This hook provides access to the shared language selection state when used within
 * a LanguageSelectedProvider. If used outside a provider, it falls back to local
 * component state with the provided default language.
 *
 * When the context value is null, it automatically initializes it with the defaultLanguage.
 *
 * @param defaultLanguage - The default language to use if no language is selected or if outside a provider
 * @returns A tuple of [selectedLanguage, setSelectedLanguage] similar to useState
 */
export function useLanguageSelected(defaultLanguage: string): [string, Dispatch<SetStateAction<string>>] {
  const state = useState<string>(defaultLanguage);
  const context = useContext(LanguageSelectedContext);
  if (!context) {
    return state;
  }
  const [selectedLang, setSelectedLang] = context;
  if (isNil(selectedLang)) {
    setSelectedLang(defaultLanguage);
  }
  return [selectedLang ?? defaultLanguage, setSelectedLang];
}
